export const months = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
]

export const currentMonthNumber = () => +new Date().getMonth() + 1
export const currentMonth = () => months[+new Date().getMonth()]
export const currentYear = () => new Date().getFullYear()

export const getDateMonth = date => months[+date.getMonth()]
export const getDateYear = date => date.getFullYear()

// Returns date in a (TODO locale specific) yyyy-mm-dd format format
export const dateToNeutralString = date => {
  let parsedDate = ""
  let dateObject

  if (typeof date == "string" && date !== "") {
    if (date.includes(".")) {
      // German format DD.MM.YYYY - convert to ISO format
      const [day, month, year] = date.split(".")
      dateObject = new Date(`${year}-${month}-${day}`)
    } else {
      dateObject = new Date(date)
    }

    // Check if the conversion resulted in a valid date
    if (isNaN(dateObject.getTime())) {
      dateObject = ""
    }
  }
  if (typeof date == "object" && date !== "") {
    dateObject = date
  }

  if (dateObject) {
    // Year of date shouldn't be larger than 9999
    if (dateObject.getFullYear() > 9999) {
      return ""
    }

    const pad = s => {
      return s < 10 ? "0" + s : s
    }
    parsedDate = [dateObject.getFullYear(), pad(dateObject.getMonth() + 1), pad(dateObject.getDate())].join("-")
  }
  return parsedDate
}

// Returns date in a readable (TODO locale specific) string format
export const dateToString = date => {
  // TODO: Variable locale. Currently I guess systems locale is taken.
  // const locale = "de-DE"
  let parsedDate = ""

  if (typeof date == "string" && date !== "") {
    const dateObject = new Date(date)
    const pad = s => {
      return s < 10 ? "0" + s : s
    }
    parsedDate = [pad(dateObject.getDate()), pad(dateObject.getMonth() + 1), dateObject.getFullYear()].join(".")
  }

  return parsedDate
}

/**
 * Formats a date-only `yyyy-MM-dd` string (a backend `LocalDate`) as German `dd.MM.yyyy`, padding
 * day and month to two digits.
 *
 * Deliberately string-only, unlike [dateToString]: parsing `"2026-05-14"` with `new Date()` yields
 * UTC midnight, which `getDate()` then renders in local time — one day earlier in every timezone
 * behind UTC. A value that carries no time at all must never be shifted by one.
 *
 * TODO: Variable locale, same as the helpers above.
 */
export const isoDateToString = (isoDate: string): string => {
  if (typeof isoDate !== "string") return ""
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(isoDate.trim())
  // Anything that is not a plain ISO date is passed through untouched rather than mangled.
  if (!match) return isoDate
  const [, year, month, day] = match
  return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`
}

/**
 * The other way round: "05.10.2026" (as a `LewDateInput` hands its rules the shown text, "5.10.2026" too) as
 * "2026-10-05"; anything else as it came, for the field's own rule to judge.
 */
export const displayDateToIso = (text: string): string => {
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(String(text ?? "").trim())
  return match ? `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}` : text
}

/**
 * The weekday of a date-only `yyyy-MM-dd` string, as the backend's `WEEKDAYS` enum names it.
 *
 * Same hazard as [isoDateToString], and the same answer: `new Date("2026-08-31").getDay()` parses the
 * string as UTC midnight and then reads the day back in *local* time, so every timezone behind UTC
 * gets the previous weekday. That is not cosmetic where a weekday selects a working-time model's
 * target hours — it would measure Monday's work against Sunday's target. The arithmetic is therefore
 * done entirely in UTC, on a value that never had a timezone to begin with.
 *
 * Returns null for anything that is not a plain ISO date.
 */
export const isoDateWeekday = (isoDate: string): string | null => {
  if (typeof isoDate !== "string") return null
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(isoDate.trim())
  if (!match) return null
  const [, year, month, day] = match
  const names = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"]
  return names[new Date(Date.UTC(+year, +month - 1, +day)).getUTCDay()]
}

/**
 * The weekday of a plain ISO date in the reader's language — "Mo", "Tue".
 *
 * Same UTC arithmetic as [isoDateWeekday] and for the same reason: read back in local time, every
 * timezone behind UTC labels the day before. Returns "" for anything that is not a plain ISO date,
 * so callers can render it unconditionally.
 */
export const isoDateWeekdayShort = (isoDate: string, locale: string): string => {
  if (typeof isoDate !== "string") return ""
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(isoDate.trim())
  if (!match) return ""
  const [, year, month, day] = match
  return new Date(Date.UTC(+year, +month - 1, +day)).toLocaleDateString(locale, {
    weekday: "short",
    timeZone: "UTC",
  })
}

/**
 * Locale-aware numeric date. Day and month are always padded to two digits, so German renders
 * `24.07.2027` (not the `24.7.2027` a bare `toLocaleDateString(locale)` produces) and English
 * renders `07/24/2027`.
 *
 * Returns "" for empty/invalid input so callers can supply their own placeholder.
 */
export const localeDateToString = (value: string | number | Date, locale: string): string => {
  if (value === "" || value == null) return ""
  const date = new Date(value)
  if (isNaN(date.getTime())) return ""
  return date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" })
}

// Returns date with time in a readable (TODO locale specific) string format
export const dateTimeToString = dateTime => formatDateTime(dateTime, true)

/**
 * Same as [dateTimeToString] but without seconds: `dd.MM.yyyy HH:mm`.
 *
 * For audit metadata (created/last modified) the second is noise — nobody acts on it, and it only
 * makes the line harder to scan.
 */
export const dateTimeToShortString = dateTime => formatDateTime(dateTime, false)

const formatDateTime = (dateTime, withSeconds: boolean) => {
  // TODO: Variable locale. Currently I think systems locale is taken.
  // const locale = "de-DE"
  let parsedDateTime = ""

  let dateObject
  if (typeof dateTime == "object") {
    dateObject = dateTime
  }
  if (typeof dateTime == "string" && dateTime !== "") {
    dateObject = new Date(dateTime)
  }
  if (dateObject != undefined) {
    const pad = s => {
      return s < 10 ? "0" + s : s
    }
    const parsedDate = [pad(dateObject.getDate()), pad(dateObject.getMonth() + 1), dateObject.getFullYear()].join(".")
    const timeParts = [pad(dateObject.getHours()), pad(dateObject.getMinutes())]
    if (withSeconds) {
      timeParts.push(pad(dateObject.getSeconds()))
    }
    parsedDateTime = `${parsedDate} ${timeParts.join(":")}`
  }

  return parsedDateTime
}

/**
 * Calculates the middle time of the two params.
 * @param fromTime {string}
 * @param toTime {string}
 * @returns {{hh: string, mm: string, ss: string, msec: string, shortTime: string, time: string}}
 */
export function getTimeInBetween(fromTime, toTime) {
  const date = "1970-01-01"
  const fromDate: Date = new Date(`${date}T${fromTime}`)
  const toDate: Date = new Date(`${date}T${toTime}`)

  // the following is to handle cases where the times are on the opposite side of
  // midnight e.g. when you want to get the difference between 9:00 PM and 5:00 AM
  if (toDate < fromDate) {
    toDate.setDate(toDate.getDate() + 1)
  }

  const diff = (toDate.getTime() - fromDate.getTime()) / 2
  const diffTime = new Date(diff)
  diffTime.setUTCHours(
    diffTime.getUTCHours() + fromDate.getUTCHours(),
    diffTime.getUTCMinutes() + fromDate.getUTCMinutes(),
    diffTime.getUTCSeconds() + fromDate.getUTCSeconds(),
    diffTime.getUTCMilliseconds() + fromDate.getUTCMilliseconds()
  )

  const hh = parseTimeString(diffTime.getHours())
  const mm = parseTimeString(diffTime.getMinutes())
  const ss = parseTimeString(diffTime.getSeconds())
  const msec = parseTimeString(diffTime.getMilliseconds())

  return {
    hh,
    mm,
    ss,
    msec,
    time: `${hh}:${mm}:${ss}:${msec}`,
    shortTime: `${hh}:${mm}`,
  }
}

/**
 * rounds param upwards and adds a zero digit in front
 * @param number {number}
 * @returns {string}
 */
function parseTimeString(number) {
  const roundedNumber = Math.floor(number)
  if (roundedNumber < 10) {
    return `0${roundedNumber}`
  } else {
    return `${roundedNumber}`
  }
}

/**
 * @param epoch {number} (dateTimeEpoch)
 * @returns {{hh: number, mm: number, ss: number, msec, time: string}}
 */

function _getTimeOfEpoch(epoch) {
  let msec = epoch
  const hh = Math.floor(msec / 1000 / 60 / 60)
  msec -= hh * 1000 * 60 * 60
  const mm = Math.floor(msec / 1000 / 60)
  msec -= mm * 1000 * 60
  const ss = Math.floor(msec / 1000)
  msec -= ss * 1000

  return {
    hh,
    mm,
    ss,
    msec,
    time: `${hh}:${mm}:${ss}:${msec}`,
  }
}

/**
 * @param date YYYY-MM-DD date string
 * @param range start and end YYYY-MM-DD date string
 * @param inclusive if date is between the date inclusive the range defined ones or exclusive
 */
export const isBetweenDate = (date: string, range: { start: string; end: string }, inclusive: boolean) => {
  const dateObj = new Date(date)
  const startObj = new Date(range.start)
  const endObj = new Date(range.end)

  if (inclusive) {
    return dateObj >= startObj && dateObj <= endObj
  } else {
    return dateObj > startObj && dateObj < endObj
  }
}

/**
 * ISO-8601-style calendar week for a `yyyy-MM-dd` date, with a configurable week-start day.
 *
 * `weekStartsOn` follows the JS `Date.getUTCDay()` convention (0 = Sunday … 6 = Saturday) and
 * defaults to 1 (Monday), which is exactly ISO-8601. The numbering rule generalises ISO's "week 1
 * is the week containing the year's first Thursday": week 1 is the week whose *4th day* (the
 * majority/mid day) is the first such day of the year — for a Monday start that 4th day is Thursday,
 * for a Sunday start it is Wednesday. Late-December / early-January days can therefore belong to the
 * neighbouring year's week, so `year` is the *week-based* year (which may differ from the calendar
 * year) and `key` (`"2026-W07"`) sorts chronologically as plain text.
 *
 * We only expose Monday and Sunday starts in the UI, but the math is valid for any start day.
 * Computed in UTC so it never shifts across a day boundary due to the local timezone.
 */
export const getIsoWeek = (date: string, weekStartsOn = 1): { year: number; week: number; key: string } => {
  const [y, m, d] = date.split("-").map(Number)
  // The 4th day of the target week decides both the week number and the week-based year.
  const target = new Date(Date.UTC(y, m - 1, d))
  const dayNr = (target.getUTCDay() - weekStartsOn + 7) % 7 // 0 = week-start day … 6 = last day
  target.setUTCDate(target.getUTCDate() - dayNr + 3)
  // Jan 4 is always in week 1 for this scheme; snap it to its own week's 4th day to anchor numbering.
  const firstMidDay = new Date(Date.UTC(target.getUTCFullYear(), 0, 4))
  const firstDayNr = (firstMidDay.getUTCDay() - weekStartsOn + 7) % 7
  firstMidDay.setUTCDate(firstMidDay.getUTCDate() - firstDayNr + 3)
  const week = 1 + Math.round((target.getTime() - firstMidDay.getTime()) / (7 * 24 * 3600 * 1000))
  const year = target.getUTCFullYear()
  const key = `${year}-W${String(week).padStart(2, "0")}`
  return { year, week, key }
}
